import { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import Cookies from "js-cookie";
import Bugsnag from "@lib/bugsnag";
import NProgress from "nprogress";
import gtm from "@lib/gtm";
import ahoy from "@lib/ahoy";
import isServer from "@utils/is-server";
import { useCatalogNavigation } from "@hooks/use-catalog-navigation-hooks";
import { useCloseDrawer } from "@hooks/use-drawer";
import { useClearFlashes } from "@hooks/use-flash";
import { useRouter, getDelocalizedPath, useLocale } from "@lib/i18n/navigation-pages-router";
import {
  useCurrentUser,
  useUserHeaderLastVersion,
  useClosetHeaderLastVersion,
} from "@hooks/use-current-user";
import { useReloadUser } from "@hooks/use-modify-user";
import { useReloadCustomizableCloset } from "@hooks/use-customizable-closet";
import { useCloseCartModal } from "@hooks/use-cart";
import { logUserWithToken } from "@actions/auth-actions";
import { analytics } from "@lib/trackers/analytics";
import getFeaturedPromoCode from "@utils/coupon/featuredPromoCode";
import { getSubscriptionCoupon } from "@utils/call-api/coupons-call";
import { useAddCoupon, useTunnelSteps } from "@hooks/use-tunnel-steps";

function AppInit({
  unauthRequired,
  authRequired,
  initPromoCode,
  children,
  // eslint-disable-next-line no-shadow
  logUserWithToken,
}) {
  const [appReady, setReady] = useState(false);

  const router = useRouter();
  const user = useCurrentUser();
  const userHeaderLastVersion = useUserHeaderLastVersion();
  const reloadUser = useReloadUser();
  const closetHeaderLastVersion = useClosetHeaderLastVersion();
  const reloadCustomizableCloset = useReloadCustomizableCloset();
  const catalogNavigation = useCatalogNavigation();
  const closeCartModal = useCloseCartModal();
  const closeDrawer = useCloseDrawer();
  const clearFlashes = useClearFlashes();
  const { coupon } = useTunnelSteps();
  const addCoupon = useAddCoupon();
  const locale = useLocale();

  const redirectTo = (url) => {
    router.push(url);
  };

  const redirectToLoggedPage = (userState) => {
    if (userState === "lead") {
      redirectTo("/je-commence");
    } else {
      redirectTo("/home");
    }
  };

  const redirectToSignIn = (additionalPath) => {
    const delocalizedPath = getDelocalizedPath(additionalPath);

    if (["/", "/home"].includes(delocalizedPath)) {
      redirectTo("/sign-in");
    } else {
      redirectTo(`/sign-in?redirect=${encodeURIComponent(delocalizedPath)}`);
    }
  };

  const addPromoCodeInSessionStorage = useCallback(async () => {
    if (user) return;

    const promoCodeFromUrl = router?.query?.promo_code;

    if (
      promoCodeFromUrl &&
      (promoCodeFromUrl === coupon?.name || promoCodeFromUrl === coupon?.referringToken)
    )
      return;

    const promoCode =
      promoCodeFromUrl || sessionStorage.getItem("redeemCodeToken") || getFeaturedPromoCode(locale);

    if (!promoCode) return;

    sessionStorage.setItem("redeemCodeToken", promoCode);

    const couponRes = await getSubscriptionCoupon(promoCode);

    if (!couponRes.error_code) {
      addCoupon({ ...couponRes, referringToken: promoCode });
    }
  }, [user, router?.query?.promo_code, initPromoCode, locale]);

  async function checkForUser() {
    const jwt = Cookies.get("jwt-token");
    const loggedUser = await logUserWithToken(jwt);

    if (loggedUser) {
      Bugsnag.user = loggedUser;
      Bugsnag.addOnError((event) => {
        event.addMetadata("user", loggedUser);
      });

      // /sign-in, /sign-up
      if (unauthRequired) redirectToLoggedPage(loggedUser.state);
    } else {
      // /profile, /closets
      if (authRequired) redirectToSignIn(router.asPath);
    }

    setReady(true);
  }

  const handleRouteChangeStart = () => {
    setReady(false);
    NProgress.start();
  };

  const handleRouteChangeComplete = () => {
    clearFlashes();
    NProgress.done();

    closeDrawer();
    closeCartModal();
    setReady(true);
  };

  const handleRouteChangeError = () => NProgress.done();

  useEffect(() => {
    checkForUser();
  }, []);

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeError);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [catalogNavigation]);

  useEffect(() => {
    function sendPageViewEvents() {
      if (isServer()) return;

      const url = window.location.pathname + window.location.search;

      // eslint-disable-next-line no-undef
      if (typeof _cio !== "undefined") _cio.page(url);
      analytics.page("pageview", { page: url });
      ahoy("pageView");
      gtm(
        {
          event: "pageview",
          page: window.location.pathname,
        },
        user,
      );
    }

    if (appReady) sendPageViewEvents();
  }, [appReady]);

  useEffect(() => {
    if (userHeaderLastVersion) reloadUser();
  }, [userHeaderLastVersion]);

  useEffect(() => {
    if (closetHeaderLastVersion) reloadCustomizableCloset();
  }, [closetHeaderLastVersion]);

  useEffect(() => {
    if (initPromoCode && router?.isReady && appReady) {
      addPromoCodeInSessionStorage();
    }
  }, [initPromoCode, router?.isReady, appReady, addPromoCodeInSessionStorage]);

  if (!appReady && (unauthRequired || authRequired)) return null;

  // /sign-in, /sign-up
  if (unauthRequired && user) return null;

  // /profile, /closets
  if (authRequired && !user) return null;

  return children;
}

export default connect(null, {
  logUserWithToken,
})(AppInit);
